import { Dispatch } from "redux";
import * as cognito from "../services/cognito";
import posthog from "posthog-js";
import { classifyGoogleSignIn } from "../utils/googleSignIn";
import {
  AuthError,
  confirmError,
  isAlreadyConfirmed,
  loginError,
  signupError,
} from "../utils/authErrors";

export enum UserConstants {
  LOGIN_REQUEST = "LOGIN_REQUEST",
  LOGIN_SUCCESS = "LOGIN_SUCCESS",
  LOGIN_FAILURE = "LOGIN_FAILURE",
  LOGOUT_SUCCESS = "LOGOUT_SUCCESS",
  LOGOUT_FAILURE = "LOGOUT_FAILURE",
  SIGNUP_REQUEST = "SIGNUP_REQUEST",
  SIGNUP_SUCCESS = "SIGNUP_SUCCESS",
  SIGNUP_FAILURE = "SIGNUP_FAILURE",
  SIGNUP_CONFIRMATION_REQUIRED = "SIGNUP_CONFIRMATION_REQUIRED",
  CLEAR_AUTH_ERROR = "CLEAR_AUTH_ERROR",
  FORGOT_PASSWORD_REQUEST = "FORGOT_PASSWORD_REQUEST",
  FORGOT_PASSWORD_SUCCESS = "FORGOT_PASSWORD_SUCCESS",
  FORGOT_PASSWORD_FAILURE = "FORGOT_PASSWORD_FAILURE",
  RESET_PASSWORD_REQUEST = "RESET_PASSWORD_REQUEST",
  RESET_PASSWORD_SUCCESS = "RESET_PASSWORD_SUCCESS",
  RESET_PASSWORD_FAILURE = "RESET_PASSWORD_FAILURE",
}

export function checkLoginStatus () {
  return async (dispatch: Dispatch<AuthenticationAction>, getState: () => RootReducerState): Promise<boolean> => {
    const loggedIn = (user: User): UserDispatch => ({type: UserConstants.LOGIN_SUCCESS, user});
    const loggedOut = (user: User): UserDispatch => ({type: UserConstants.LOGOUT_SUCCESS, user});

    const {authentication} = getState();
    if (authentication.loggedIn) {
      return true;
    }

    try {
      const session = await cognito.getCurrentSession();
      if (session) {
        dispatch(loggedIn({
          username: session.username,
          email: session.email,
          anonymous: false,
          loggedIn: true,
          groups: session.groups,
        }));
        posthog.identify(session.username, { email: session.email });
        return true;
      } else {
        dispatch(loggedOut({
          username: null,
          anonymous: true,
          loggedIn: false,
        }));
        return false;
      }
    } catch (error) {
      console.error(error);
      // Mark auth as checked even on error so protected routes stop waiting.
      dispatch(loggedOut({
        username: null,
        anonymous: true,
        loggedIn: false,
      }));
      return false;
    }
  };
}

export function login (username: string, password: string) {
  return async (dispatch: Dispatch<AuthenticationAction>): Promise<string> => {
    const request = (user: User): UserDispatch => ({type: UserConstants.LOGIN_REQUEST, user});
    const success = (user: User): UserDispatch => ({type: UserConstants.LOGIN_SUCCESS, user});
    const failure = (error: AuthError): AuthenticationAction => ({
      type: UserConstants.LOGIN_FAILURE,
      error: error.message,
      errorAction: error.action,
    });

    dispatch(request({
      username,
      anonymous: false,
      loggedIn: false
    }));

    try {
      await cognito.signIn(username, password);
      // Hydrate the full session (groups + email) so user.groups is populated
      // immediately after login — otherwise isPro is false until a refresh runs
      // checkLoginStatus, making a Pro user briefly look Free (Account/Pricing).
      const session = await cognito.getCurrentSession();
      const resolvedUsername = session?.username ?? username;
      dispatch(success({
        username: resolvedUsername,
        email: session?.email,
        anonymous: false,
        loggedIn: true,
        groups: session?.groups,
      }));
      posthog.identify(resolvedUsername, { email: session?.email });
      posthog.capture("user_logged_in");
      return username;
    } catch (error: unknown) {
      const friendly = loginError(error);
      dispatch(failure(friendly));
      posthog.capture("login_failed", { error: friendly.message });
      return Promise.reject(error);
    }
  }
}

/**
 * Finish "Continue with Google" on /auth/callback: exchange the code, then
 * hydrate user state through the same LOGIN_SUCCESS path as login(). Dispatches
 * directly rather than via checkLoginStatus(), which returns early when a
 * session was already hydrated (an older login in this tab) and would keep the
 * previous account's state. Failures are rethrown for the page to explain, not
 * put in the store, so a stale callback error can't leak onto the login form.
 */
export function loginWithGoogle (params: URLSearchParams) {
  return async (dispatch: Dispatch<AuthenticationAction>): Promise<{ next: string | null }> => {
    try {
      const { next } = await cognito.completeGoogleSignIn(params);
      const session = await cognito.getCurrentSession();
      if (!session) {
        throw new cognito.GoogleSignInError("session", "Google sign-in returned an unusable session.", { next });
      }
      dispatch({
        type: UserConstants.LOGIN_SUCCESS,
        user: {
          username: session.username,
          email: session.email,
          anonymous: false,
          loggedIn: true,
          groups: session.groups,
        },
      });
      posthog.identify(session.username, { email: session.email });
      // The first Google sign-in of a brand-new account is a signup, as it is on the email
      // path (which fires user_signed_up and no user_logged_in); linking Google to an
      // existing account is still a login by an existing person.
      const kind = classifyGoogleSignIn(session.idToken);
      if (kind === "new_account") {
        posthog.capture("user_signed_up", { method: "google" });
      } else {
        posthog.capture("user_logged_in", kind === "linked_account" ? { method: "google", account_linked: true } : { method: "google" });
      }
      return { next };
    } catch (error: unknown) {
      const reason = cognito.isGoogleSignInError(error) ? (error.code ?? error.kind) : "unknown";
      posthog.capture("login_failed", { method: "google", error: reason });
      return Promise.reject(error);
    }
  }
}

export function logout () {
  return (dispatch: Dispatch<AuthenticationAction>): void => {
    const success = (): UserDispatchType => ({type: UserConstants.LOGOUT_SUCCESS});

    cognito.signOut();
    dispatch(success());
  }
}

export function forgotPassword (email: string) {
  return async (dispatch: Dispatch<AuthenticationAction>): Promise<void> => {
    dispatch({ type: UserConstants.FORGOT_PASSWORD_REQUEST });

    try {
      await cognito.forgotPassword(email);
      dispatch({ type: UserConstants.FORGOT_PASSWORD_SUCCESS });
      posthog.capture("forgot_password_requested");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to send reset code";
      dispatch({ type: UserConstants.FORGOT_PASSWORD_FAILURE, error: message });
    }
  }
}

export function confirmPassword (email: string, code: string, newPassword: string) {
  return async (dispatch: Dispatch<AuthenticationAction>): Promise<void> => {
    dispatch({ type: UserConstants.RESET_PASSWORD_REQUEST });

    try {
      await cognito.confirmForgotPassword(email, code, newPassword);
      dispatch({ type: UserConstants.RESET_PASSWORD_SUCCESS });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to reset password";
      dispatch({ type: UserConstants.RESET_PASSWORD_FAILURE, error: message });
    }
  }
}

/** "confirmationRequired": Cognito emailed a code; the page must collect it
 *  (`confirmSignup`) before the account can sign in. */
export type SignupOutcome = "signedIn" | "confirmationRequired";

const signupFailure = (error: AuthError): AuthenticationAction => ({
  type: UserConstants.SIGNUP_FAILURE,
  error: error.message,
  errorAction: error.action,
});

/** Sign a just-created account in and hydrate groups + email from the session,
 *  so user state matches the login path (see login()). */
async function signInNewAccount(
  email: string,
  password: string,
  dispatch: Dispatch<AuthenticationAction>
): Promise<void> {
  await cognito.signIn(email, password);
  const session = await cognito.getCurrentSession();
  const resolvedUsername = session?.username ?? email;
  dispatch({
    type: UserConstants.SIGNUP_SUCCESS,
    user: {
      username: resolvedUsername,
      email: session?.email ?? email,
      anonymous: false,
      loggedIn: true,
      groups: session?.groups,
    },
  });
  posthog.identify(resolvedUsername, { email: session?.email ?? email });
  posthog.capture("user_signed_up", { email: session?.email ?? email, method: "email" });
}

export function signup (email: string, password: string, turnstileToken?: string) {
  return async (dispatch: Dispatch<AuthenticationAction>): Promise<SignupOutcome> => {
    dispatch({ type: UserConstants.SIGNUP_REQUEST });

    try {
      const { userConfirmed } = await cognito.signUp(email, password, turnstileToken);
      if (!userConfirmed) {
        dispatch({ type: UserConstants.SIGNUP_CONFIRMATION_REQUIRED });
        posthog.capture("signup_confirmation_required");
        return "confirmationRequired";
      }
      // Auto-confirmed by the pre-signup Lambda (RequireEmailVerification=false).
      await signInNewAccount(email, password, dispatch);
      return "signedIn";
    } catch (error: unknown) {
      const friendly = signupError(error);
      dispatch(signupFailure(friendly));
      posthog.capture("signup_failed", { error: friendly.message });
      return Promise.reject(error);
    }
  }
}

/** Enter the emailed code, then sign in. Reached from the signup form and from
 *  a login attempt on an account that never confirmed. */
export function confirmSignup (email: string, code: string, password: string) {
  return async (dispatch: Dispatch<AuthenticationAction>): Promise<SignupOutcome> => {
    dispatch({ type: UserConstants.SIGNUP_REQUEST });

    try {
      await cognito.confirmSignUp(email, code);
    } catch (error: unknown) {
      if (!isAlreadyConfirmed(error)) {
        const friendly = confirmError(error);
        dispatch(signupFailure(friendly));
        posthog.capture("signup_confirm_failed", { error: friendly.message });
        return Promise.reject(error);
      }
    }

    try {
      await signInNewAccount(email, password, dispatch);
      return "signedIn";
    } catch (error: unknown) {
      // Confirmed but couldn't sign in (e.g. a mistyped password on the login
      // path) — the login copy, not the code copy, explains that.
      dispatch(signupFailure(loginError(error)));
      return Promise.reject(error);
    }
  }
}

/** Returns null when a new code was sent, or the copy to show when it wasn't. */
export async function resendSignupCode (email: string): Promise<AuthError | null> {
  try {
    await cognito.resendConfirmationCode(email);
    posthog.capture("signup_code_resent");
    return null;
  } catch (error: unknown) {
    if (isAlreadyConfirmed(error)) {
      return { message: "This account is already confirmed — log in instead.", action: "login" };
    }
    return confirmError(error);
  }
}
